import { startCase } from 'es-toolkit';
import React from 'react';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { hasFreeSubscription, UpgradeNotification } from '@paragon/login-tools';

import { showUpgradeAccountDialog } from '../../../../../../store/actions/popupActions';
import { useAppDispatch, useAppSelector } from '../../../../../hooks';
import { MultiValueBooleanInput } from '../../multi-value-controls/MultiValueBooleanInput';
import { MultiValueNumberInput } from '../../multi-value-controls/MultiValueNumberInput';
import { MultiValueSelectInput } from '../../multi-value-controls/MultiValueSelectInput';
import { LoadSettingsControlProps } from '../LoadSettingsControls';
import { DurationFactorSetting } from './DurationFactorSettings';

const sectionSx = { p: 1 } as const;
const inputBoxSx = { width: '45%', marginInline: 1 } as const;

export function TornadoLoadSettings({ disabled, loadSettingsArray, setLoadSettingsValues }: LoadSettingsControlProps) {
  const dispatch = useAppDispatch();

  // snow loading is not available to free users
  const isFreeUser = useAppSelector(hasFreeSubscription);
  if (isFreeUser) disabled = true;

  const tornadoEnclosureClassifications = loadSettingsArray.map((loadSettings) =>
    loadSettings.reclassifyEnclosedAsPartiallyEnclosedForTornadoLoads &&
    loadSettings.enclosureClassification === 'Enclosed'
      ? 'PartiallyEnclosed'
      : loadSettings.enclosureClassification,
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {isFreeUser && (
        <UpgradeNotification
          sx={{ marginBottom: 1 }}
          briefDescription="tornado loading"
          onUpgradeClick={() => dispatch(showUpgradeAccountDialog())}
        />
      )}

      <Box sx={sectionSx}>
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <MultiValueNumberInput
            sx={inputBoxSx}
            values={loadSettingsArray.map((loadSettings) => loadSettings.tornadoSpeed)}
            submit={(tornadoSpeed) => setLoadSettingsValues({ tornadoSpeed })}
            label="Tornado Speed (V_T)"
            unit="mph"
            disabled={disabled}
          />
          <MultiValueNumberInput
            sx={inputBoxSx}
            values={loadSettingsArray.map((loadSettings) => loadSettings.tornadoGustEffectFactor)}
            submit={(tornadoGustEffectFactor) => setLoadSettingsValues({ tornadoGustEffectFactor })}
            label="Tornado Gust-Effect Factor (G_T)"
            disabled={disabled}
          />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-evenly' }}>
          <DurationFactorSetting
            sx={inputBoxSx}
            loadType="tornado"
            loadSettingsArray={loadSettingsArray}
            setLoadSettingsValues={setLoadSettingsValues}
            disabled={disabled}
          />
        </Box>
      </Box>
      <Box sx={{ ...sectionSx, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box>
          <MultiValueBooleanInput
            values={loadSettingsArray.map(
              (loadSettings) => loadSettings.reclassifyEnclosedAsPartiallyEnclosedForTornadoLoads,
            )}
            onChange={(reclassifyEnclosedAsPartiallyEnclosedForTornadoLoads) =>
              setLoadSettingsValues({ reclassifyEnclosedAsPartiallyEnclosedForTornadoLoads })
            }
            label="Treat building as Partially Enclosed for tornado loads (if Enclosed)"
            disabled={
              disabled || loadSettingsArray.every((loadSettings) => loadSettings.enclosureClassification !== 'Enclosed')
            }
          />
          <TooltipIconButton
            title={
              'Enclosed buildings with unprotected glazed openings may need to be reclassified as Partially Enclosed ' +
              'for tornado loads if breaches of some of the glazed openings would change the classification.'
            }
          >
            <HelpIcon />
          </TooltipIconButton>
        </Box>
        <MultiValueSelectInput
          sx={{ ...inputBoxSx, marginTop: 1 }}
          property="Tornado Enclosure Classification"
          options={tornadoEnclosureClassifications}
          values={tornadoEnclosureClassifications}
          getName={startCase}
          disabled
          fullWidth={false}
        />
      </Box>
      <Box sx={{ ...sectionSx, alignSelf: 'center' }}>
        <MultiValueBooleanInput
          values={loadSettingsArray.map((loadSettings) => loadSettings.essentialFacilityOrNecessaryThereto)}
          onChange={(essentialFacilityOrNecessaryThereto) =>
            setLoadSettingsValues({ essentialFacilityOrNecessaryThereto })
          }
          label="Treat as Essential Facility"
          disabled={disabled}
        />
        <TooltipIconButton title="Building is an Essential Facility or is required to maintain the functionality of an Essential Facility.">
          <HelpIcon />
        </TooltipIconButton>
      </Box>
    </Box>
  );
}
